import React from 'react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Cookie Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Learn about how we use cookies and similar technologies to enhance your experience on our website.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: January 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="prose max-w-none">
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What Are Cookies?</h2>
            
            <p className="text-gray-700 mb-6">
              Cookies are small text files that are stored on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences, 
              analyzing how you use our site, and personalizing content.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Essential Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are necessary for the website to function properly. They enable basic functions 
              like page navigation, access to secure areas, and remembering your login status.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Session management cookies</li>
              <li>Authentication cookies</li>
              <li>Security cookies</li>
              <li>Load balancing cookies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Performance Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies collect information about how you use our website, such as which pages 
              you visit most often and if you get error messages. This helps us improve our website's performance.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Google Analytics cookies</li>
              <li>Page load time tracking</li>
              <li>Error reporting cookies</li>
              <li>User behavior analytics</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Functional Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies enable the website to provide enhanced functionality and personalization. 
              They may be set by us or by third-party providers whose services we have added to our pages.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Language preference cookies</li>
              <li>Theme and display preferences</li>
              <li>Course progress tracking</li>
              <li>User interface customization</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Marketing Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are used to track visitors across websites. The intention is to display 
              ads that are relevant and engaging for individual users.
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Advertising platform cookies</li>
              <li>Social media integration cookies</li>
              <li>Retargeting cookies</li>
              <li>Conversion tracking cookies</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Third-Party Cookies</h2>
            
            <p className="text-gray-700 mb-6">
              We use third-party services that may set their own cookies on your device. These include:
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Third-Party Services</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800">Google Analytics</h4>
                  <p className="text-gray-600 text-sm">Tracks website usage and performance metrics</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Social Media Platforms</h4>
                  <p className="text-gray-600 text-sm">Enables social sharing and integration features</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Payment Processors</h4>
                  <p className="text-gray-600 text-sm">Secures payment transactions and fraud prevention</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Email Marketing</h4>
                  <p className="text-gray-600 text-sm">Manages newsletter subscriptions and communications</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Cookie Duration</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Session Cookies</h3>
            <p className="text-gray-700 mb-6">
              These cookies are temporary and are deleted when you close your browser. They are used 
              to maintain your session while you navigate through our website.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Persistent Cookies</h3>
            <p className="text-gray-700 mb-6">
              These cookies remain on your device for a set period or until you delete them. They help 
              us recognize you when you return to our website and remember your preferences.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Managing Your Cookie Preferences</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              You can control and delete cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>View and delete existing cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block third-party cookies</li>
              <li>Set preferences for cookie acceptance</li>
              <li>Receive notifications when cookies are set</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Cookie Consent</h3>
            <p className="text-gray-700 mb-6">
              When you first visit our website, you'll see a cookie consent banner. You can choose 
              which types of cookies to accept or reject. You can change your preferences at any time 
              through our cookie settings.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Impact of Disabling Cookies</h2>
            
            <p className="text-gray-700 mb-6">
              If you choose to disable cookies, some features of our website may not function properly:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>You may need to log in repeatedly</li>
              <li>Your course progress may not be saved</li>
              <li>Personalized content may not be available</li>
              <li>Some interactive features may not work</li>
              <li>Website performance may be affected</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Mobile Devices</h2>
            
            <p className="text-gray-700 mb-6">
              Cookies work similarly on mobile devices as they do on desktop computers. You can manage 
              cookies through your mobile browser settings or device privacy settings.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Updates to This Policy</h2>
            
            <p className="text-gray-700 mb-6">
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for other operational, legal, or regulatory reasons. We will notify you of any 
              material changes by posting the updated policy on this page.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Contact Us</h2>
            
            <p className="text-gray-700 mb-6">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cookie Policy Contact</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> karabacakhilal1.1@gmail.com</p>
                <p><strong>Subject Line:</strong> Cookie Policy Inquiry</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Legal Compliance:</strong> This Cookie Policy complies with applicable privacy 
                laws including GDPR, CCPA, and ePrivacy Directive requirements for cookie consent and transparency.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
